import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmtDateTime } from "./docConstants";
import HandoffDialog from "./HandoffDialog";

export default function SigningPanel({ docId, onChanged }) {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [sg, setSg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const [pendingHandoff, setPendingHandoff] = useState(false);
  const [internalNote, setInternalNote] = useState("");
  const [externalNote, setExternalNote] = useState("");
  const [link, setLink] = useState("");
  const [meetingId, setMeetingId] = useState(null);

  const loadSigning = useCallback(async () => {
    try {
      const { data } = await api.get(`/doc-center/documents/${docId}/signing`);
      setSg(data.signing);
      setInternalNote(data.signing.internal_signing_notes || "");
      setExternalNote(data.signing.external_signing_note || "");
      setLink(data.signing.external_signing_link || "");
      setMeetingId(data.signing.related_meeting_id || null);
    } catch (e) { /* */ }
  }, [docId]);

  useEffect(() => {
    api.get("/doc-center/signing/meta").then((r) => setMeta(r.data)).catch(() => {});
    loadSigning();
  }, [loadSigning]);

  useEffect(() => {
    if (!pendingHandoff) return;
    const t = setTimeout(() => { setHandoff(true); setPendingHandoff(false); }, 300);
    return () => clearTimeout(t);
  }, [pendingHandoff]);

  const patch = async (payload, msg, { openHandoff } = {}) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/doc-center/documents/${docId}/signing`, payload);
      setSg(data.signing);
      if (msg) toast.success(msg);
      onChanged?.();
      if (openHandoff) setPendingHandoff(true);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const onDone = () => { loadSigning(); onChanged?.(); };
  const uploadSigned = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/doc-center/documents/${docId}/signing/signed-upload`, fd);
      toast.success("Signed document uploaded.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  if (!sg || !meta) return null;

  return (
    <div className="rounded-xl border border-[#E6EDF2] bg-white p-4 space-y-3" data-testid="signing-panel">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-[13px] font-bold text-[#1C4358] flex items-center gap-1.5"><Icons.PenLine size={15} /> Signing coordination</h2>
        <Button size="sm" className="h-8 bg-[#7C3AED] hover:bg-[#6b30d0] text-white text-[12px]" data-testid="signing-handoff-btn" onClick={() => setHandoff(true)}>
          <Icons.Share2 size={13} className="mr-1" /> Signing handoff
        </Button>
      </div>

      <div className="rounded-lg bg-[#FFF7ED] border border-[#FED7AA] px-3 py-2 flex items-start gap-2" data-testid="signing-disclaimer">
        <Icons.ShieldAlert size={14} className="text-[#B7791F] shrink-0 mt-0.5" />
        <span className="text-[11px] text-[#92600E]">{meta.disclaimer}</span>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[12px]">Method</Label>
          <Select value={sg.signing_method} onValueChange={(v) => patch({ signing_method: v }, "Signing method updated.")}>
            <SelectTrigger className="h-8 text-[12px]" data-testid="signing-method"><SelectValue /></SelectTrigger>
            <SelectContent>{meta.methods.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[12px]">Status</Label>
          <Select value={sg.signing_status} onValueChange={(v) => patch({ signing_status: v }, "Signing status updated.", { openHandoff: v === "ready_for_signing" })}>
            <SelectTrigger className="h-8 text-[12px]" data-testid="signing-status"><SelectValue /></SelectTrigger>
            <SelectContent>{meta.statuses.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      {sg.professional_name && (
        <div className="text-[12px] text-[#4B5563]" data-testid="signing-professional"><Icons.UserCheck size={13} className="inline mr-1 text-[#2F7D5A]" /> Assigned: <span className="font-semibold">{sg.professional_name}</span></div>
      )}
      {meetingId && (
        <button onClick={() => navigate(`/meetings/${meetingId}`)} className="text-[12px] text-[#1C4358] hover:underline flex items-center gap-1" data-testid="signing-meeting-link">
          <Icons.Video size={13} /> Open linked meeting room
        </button>
      )}

      {/* e-sign placeholder */}
      <div className="rounded-lg border border-[#E6EDF2] p-2.5 space-y-2" data-testid="signing-esign">
        <div className="text-[11px] font-semibold text-[#6B7280] uppercase tracking-wide">External e-sign (placeholder)</div>
        <div className="grid grid-cols-2 gap-2">
          <div><Label className="text-[11px]">Provider</Label>
            <Select value={sg.external_signing_provider} onValueChange={(v) => patch({ external_signing_provider: v }, "Provider set.")}>
              <SelectTrigger className="h-8 text-[12px]" data-testid="signing-provider"><SelectValue /></SelectTrigger>
              <SelectContent>{meta.providers.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-[11px]">Signing link</Label>
            <div className="flex gap-1">
              <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="https://…" className="h-8 text-[12px]" data-testid="signing-link" />
              <Button size="sm" variant="ghost" className="h-8 px-2 text-[#1C4358]" data-testid="signing-link-save" disabled={busy} onClick={() => patch({ external_signing_link: link }, "Signing link saved.")}><Icons.Check size={14} /></Button>
            </div>
          </div>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-[#D6E0E7]" data-testid="signing-mark-sent" disabled={busy} onClick={() => patch({ sent_for_signing_at: "" }, "Marked sent externally.")}>Mark sent externally</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-[#2F7D5A]/40 text-[#2F7D5A]" data-testid="signing-mark-signed" disabled={busy} onClick={() => patch({ signing_status: "signed_externally" }, "Marked signed externally.")}>Signed externally</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-[#2F7D5A]/40 text-[#2F7D5A]" data-testid="signing-mark-completed" disabled={busy} onClick={() => patch({ completed_at: "", signing_status: "completed" }, "Marked completed outside system.")}>Completed outside</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-[#9C1E22]/40 text-[#9C1E22]" data-testid="signing-cancel" disabled={busy} onClick={() => patch({ signing_status: "cancelled" }, "Signing cancelled.")}>Cancel</Button>
          <Button size="sm" variant="outline" className="h-7 text-[11px] border-[#6B7280]/40 text-[#6B7280]" data-testid="signing-not-required" disabled={busy} onClick={() => patch({ signing_status: "not_required", signing_method: "not_required" }, "Marked not required.")}>Not required</Button>
        </div>
        <div className="text-[10.5px] text-[#94A3B8]">
          {sg.sent_for_signing_at && <>Sent: {fmtDateTime(sg.sent_for_signing_at)} · </>}
          {sg.signed_at && <>Signed: {fmtDateTime(sg.signed_at)} · </>}
          {sg.completed_at && <>Completed: {fmtDateTime(sg.completed_at)}</>}
        </div>
      </div>

      {/* signed doc upload */}
      <div>
        <label className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[#1C4358] cursor-pointer hover:underline" data-testid="signing-upload-label">
          <Icons.Upload size={13} /> Upload signed document
          <input type="file" className="hidden" data-testid="signing-upload-input" onChange={(e) => uploadSigned(e.target.files?.[0])} />
        </label>
      </div>

      {/* notes */}
      <div>
        <Label className="text-[12px] flex items-center gap-1 text-[#9C1E22]"><Icons.Lock size={11} /> Internal signing note (staff only)</Label>
        <Textarea rows={2} value={internalNote} onChange={(e) => setInternalNote(e.target.value)} className="text-[13px]" data-testid="signing-internal-note" />
        <Button size="sm" variant="ghost" className="h-7 mt-1 text-[12px] text-[#1C4358]" data-testid="signing-save-internal" disabled={busy} onClick={() => patch({ internal_signing_notes: internalNote }, "Internal note saved.")}>Save internal note</Button>
      </div>
      <div>
        <Label className="text-[12px] flex items-center gap-1 text-[#2C6480]"><Icons.MessageSquare size={11} /> External-visible signing note</Label>
        <Textarea rows={2} value={externalNote} onChange={(e) => setExternalNote(e.target.value)} className="text-[13px]" data-testid="signing-external-note" />
        <Button size="sm" variant="ghost" className="h-7 mt-1 text-[12px] text-[#1C4358]" data-testid="signing-save-external" disabled={busy} onClick={() => patch({ external_signing_note: externalNote }, "Response saved.")}>Save response</Button>
      </div>

      <HandoffDialog open={handoff} onOpenChange={setHandoff} docId={docId} meta={meta} onDone={onDone} />
    </div>
  );
}
